/**
 * Generates a CSV template string for Unit Bulk Upload
 * @param {string} programName - Optional context
 * @param {number} moduleNum - Optional context
 * @returns {string} CSV content
 */
export const generateUnitTemplate = (programName = 'Program', moduleNum = 1) => {
    const headers = [
        'Code',
        'Name',
        'Credits',
        'HoursPerWeek',
        'SessionLengthMinutes',
        'UnitType',
        'RoomType'
    ];

    // Example rows providing guidance
    const examples = [
        `CS${moduleNum}01,Introduction to ${programName},3,4,90,Core,Lecture`,
        `CS${moduleNum}02,Advanced Topic,3,4,120,Core,Laboratory`
    ];

    return [headers.join(','), ...examples].join('\n');
};

export const parseUnitWorkbook = async (file) => {
    const ExcelJS = (await import('exceljs')).default;
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(await file.arrayBuffer());
    const sheet = workbook.getWorksheet('Units Upload') || workbook.worksheets[0];
    if (!sheet) throw new Error('The workbook has no Units Upload worksheet');
    const headers = {};
    sheet.getRow(4).eachCell((cell, column) => { headers[String(cell.value || '').trim().toLowerCase()] = column; });
    const required = ['programme code','programme level','module','unit code','unit name','classification','required room type'];
    const missing = required.filter(header => !headers[header]);
    if (missing.length) throw new Error(`Missing template columns: ${missing.join(', ')}`);
    const value = (row, header) => String(row.getCell(headers[header]).value ?? '').trim();
    const units = [];
    sheet.eachRow((row, rowNumber) => {
        if (rowNumber < 5) return;
        const code = value(row, 'unit code');
        const name = value(row, 'unit name');
        if (!code && !name) return;
        if (!code || !name) throw new Error(`Row ${rowNumber}: Unit Code and Unit Name are both required`);
        units.push({
            rowNumber,
            programCode: value(row, 'programme code'),
            programLevel: Number(value(row, 'programme level').match(/[3-7]/)?.[0]),
            module: Number(row.getCell(headers.module).value),
            code, name,
            unitType: value(row, 'classification'),
            creditHours: Number(row.getCell(headers['credit hours']).value || 3),
            hoursPerWeek: Number(row.getCell(headers['hours per week']).value || 4),
            sessionLengthMinutes: Number(row.getCell(headers['session minutes']).value || 90),
            roomType: value(row, 'required room type'),
            allowCombinedDelivery: value(row, 'combined delivery').toLowerCase() === 'yes',
            teachingGroupCode: value(row, 'teaching group code'),
            isActive: value(row, 'active').toLowerCase() !== 'no'
        });
    });
    if (!units.length) throw new Error('No unit rows were found in the workbook');
    return units;
};

/**
 * Parses a CSV file for Unit Bulk Upload
 * @param {File} file - The file to parse
 * @returns {Promise<Array>} Array of unit objects
 */
export const parseUnitCSV = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const text = e.target.result;
                const lines = text.split(/\r?\n/).filter(line => line.trim());

                if (lines.length < 2) {
                    reject(new Error('File is empty or missing headers'));
                    return;
                }

                // Normalize headers
                const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, ''));

                const units = [];
                const errors = [];

                for (let i = 1; i < lines.length; i++) {
                    // Be careful with splitting only by comma if fields contain commas. 
                    // For now, simple split, assuming strictly formatted CSV.
                    const values = lines[i].split(',').map(v => v.trim());

                    if (values.length < headers.length) {
                        // Skip empty or malformed lines
                        continue;
                    }

                    const unit = {};
                    headers.forEach((header, index) => {
                        const value = values[index];

                        switch (header) {
                            case 'code': unit.code = value; break;
                            case 'name': unit.name = value; break;
                            case 'credits': unit.creditHours = parseInt(value) || 3; break;
                            case 'hoursperweek': unit.hoursPerWeek = parseInt(value) || 4; break;
                            case 'sessionlengthminutes': unit.sessionLengthMinutes = parseInt(value) || 90; break;
                            case 'unittype': unit.unitType = value || 'Core'; break;
                            case 'roomtype': unit.roomType = value || 'Lecture'; break;
                            // Add capacity or other fields if in template
                        }
                    });

                    // Basic validation
                    if (unit.code && unit.name) {
                        units.push(unit);
                    }
                }

                resolve(units);
            } catch (err) {
                reject(err);
            }
        };

        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(file);
    });
};

/**
 * Converts unit data to CSV for export
 * @param {Array} units 
 * @returns {string}
 */
export const unitsToCSV = (units) => {
    const headers = ['Code', 'Name', 'Type', 'Credits', 'Module', 'Program'];
    const rows = units.map(u => [
        u.code,
        `"${u.name}"`, // Quote name in case of commas
        u.unitType,
        u.creditHours,
        u.module,
        `"${u.program?.name || ''}"`
    ].join(','));

    return [headers.join(','), ...rows].join('\n');
};
